import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AuthContext from "../auth/AuthContext";
import PostFormDialog from "../components/PostFormDialog";
import PostList from "../components/PostList";
import { createRecipe, getRecipe, getRecipes } from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

function Recipes() {
  useDocumentTitle("Opskrifter");
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_admin;
  const [searchParams, setSearchParams] = useSearchParams();

  const author = searchParams.get("author") || "";
  const q = searchParams.get("q") || "";
  const root = searchParams.get("root") || "";

  const [searchInput, setSearchInput] = useState(q);
  const [posts, setPosts] = useState([]);
  const [rootRecipe, setRootRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const queryParams = useMemo(() => {
    const params = {};
    if (author) params.author = author;
    if (q) params.q = q;
    if (root) params.root = root;
    return params;
  }, [author, q, root]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getRecipes(queryParams);
      setPosts(Array.isArray(data) ? data : []);
      if (root) {
        try {
          const parent = await getRecipe(root);
          setRootRecipe(parent);
        } catch {
          setRootRecipe(null);
        }
      } else {
        setRootRecipe(null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente opskrifter");
    } finally {
      setLoading(false);
    }
  }, [queryParams, user?.user_uuid, root]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    updateParam("q", searchInput.trim());
  };

  const handleCreate = async (payload) => {
    await createRecipe(payload);
    setCreateOpen(false);
    await loadPosts();
  };

  const hasFilters = Boolean(author || q);
  const viewingVariants = Boolean(root);

  return (
    <Box sx={{ position: "relative", py: { xs: 1, sm: 2 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 2 }}>
        {viewingVariants ? "Varianter" : "Opskrifter"}
      </Typography>

      {viewingVariants ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/recipes"
            variant="text"
            sx={{ alignSelf: "flex-start", px: 0 }}
          >
            ← Alle opskrifter
          </Button>
          {rootRecipe ? (
            <Typography variant="body1">
              Varianter af{" "}
              <Typography
                component={RouterLink}
                to={`/recipes/${rootRecipe.permalink || rootRecipe.recipe_uuid}`}
                sx={{ fontWeight: 700, color: "primary.main", textDecoration: "none" }}
              >
                {rootRecipe.title}
              </Typography>
            </Typography>
          ) : null}
        </Stack>
      ) : null}

      {!viewingVariants ? (
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2 }}>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Søg opskrifter"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Ryd søgning"
                    onClick={() => {
                      setSearchInput("");
                      updateParam("q", "");
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      ) : null}

      {hasFilters && !viewingVariants ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {author ? (
            <Chip label={`Forfatter: ${author}`} onDelete={() => updateParam("author", "")} size="small" />
          ) : null}
          {q ? <Chip label={`Søgning: ${q}`} onDelete={() => updateParam("q", "")} size="small" /> : null}
        </Stack>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <PostList
          posts={posts}
          emptyMessage={
            viewingVariants
              ? "Ingen synlige varianter."
              : hasFilters
                ? "Ingen opskrifter matcher filtrene."
                : "Ingen opskrifter endnu."
          }
          getPostLink={(item) =>
            `/recipes/${item.preferred_permalink || item.permalink || item.recipe_uuid}`
          }
        />
      )}

      {isAdmin && !viewingVariants ? (
        <Fab
          color="primary"
          aria-label="Tilføj opskrift"
          onClick={() => setCreateOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <RestaurantMenuIcon />
        </Fab>
      ) : null}

      <PostFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        title="Ny opskrift"
        submitLabel="Gem"
        postType="recipe"
        hideTemplate
        initial={{
          title: "",
          body: "",
          instructions: "",
          yield_amount: "",
          cook_time: "",
          visibility: "private",
          composition: "recipe",
          tags: "",
          ingredient_groups: [],
        }}
      />
    </Box>
  );
}

export default Recipes;
